import { Card } from '@material-ui/core';
import React from 'react';
import {
  BookOpen,
  Box,
  Code,
  DollarSign,
  Download,
  Edit,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  Filter,
  GitMerge,
  Grid as GridIcon,
  Hash,
  Layers,
  Layout,
  Link as LinkIcon,
  Lock,
  Mail,
  Minimize2,
  MousePointer,
  Percent,
  PieChart,
  RefreshCw,
  Repeat,
  Rewind,
  Search,
  Settings,
  ShoppingCart,
  Shuffle,
  Target,
  Terminal,
  Trello,
  TrendingDown,
  TrendingUp,
  Users,
  Zap
} from 'react-feather';
import styled from '../styled';
import { COLORS } from '../themes/color';

const FeatureLink = styled('div')`
  color: ${(p) => p.theme.palette.common.black};
  font-size: 20px;
  line-height: 36px;
  font-weight: bold;
  position: relative;
  display: block;
  transition: position;
  margin-top: ${(p) => p.theme.spacing(3)}px;
`;

export const FeatureCardGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(4)}px;
  grid-row-gap: ${(p) => p.theme.spacing(4)}px;
  justify-content: center;
  max-width: 1100px;
  margin: 0 auto 48px;
  padding: ${(p) => p.theme.spacing(2)}px;

  @media (max-width: 800px) {
    padding: ${(p) => p.theme.spacing(1)}px;
  }

  p {
    color: #4f4f4f;
    font-size: 16px;
    line-height: 27px;
  }

  h3 {
    font-weight: normal;
    font-size: 20px;
    line-height: 36px;
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(Card)`
  background: #ffffff;
  border-radius: 0;
  box-shadow: 0 2px 10px 2px rgba(0, 0, 0, 0.03);
  border: 1px solid #f9f9f9;
  padding: ${(p) => p.theme.spacing(4)}px ${(p) => p.theme.spacing(2.5)}px
    ${(p) => p.theme.spacing(2)}px;
`;

const GridWrapper = styled('div')`
  max-width: 1100px;
  margin: 0 auto 48px;
  padding: ${(p) => p.theme.spacing(2)}px;

  @media (max-width: 800px) {
    padding: ${(p) => p.theme.spacing(2)}px;
  }
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(8)}px;
  grid-row-gap: ${(p) => p.theme.spacing(8)}px;
  justify-content: center;

  p {
    color: #4f4f4f;
    font-size: 16px;
    line-height: 27px;
  }

  a {
    border-bottom: 1px dashed;
  }

  h3 {
    font-weight: normal;
    font-size: 20px;
    line-height: 36px;
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureIcon = styled('div')`
  width: 50px;
  height: 50px;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.TEXT};
  background-color: ${COLORS.PAPER_DARK};
`;

export const TeamsWorkspaces = () => (
  <div>
    <FeatureIcon>
      <GridIcon size={20} />
    </FeatureIcon>
    <h3>Organize groups of sites into separate workspaces</h3>
    <p>
      Create separate workspaces for different groups of sites, and share team
      members across them.
    </p>
  </div>
);

export const TeamsWorkspaceSwitcher = () => (
  <div>
    <FeatureIcon>
      <Repeat size={20} />
    </FeatureIcon>
    <h3>Seamlessly switch between workspaces</h3>
    <p>
      Easily switch between workspaces without having to log in and out of your
      account.
    </p>
  </div>
);

export const TeamsAccounts = () => (
  <div>
    <FeatureIcon>
      <Users size={20} />
    </FeatureIcon>
    <h3>Invite your team to your Affilimate account</h3>
    <p>
      Each user has their login credentials to access the account, view reports,
      and perform common tasks.
    </p>
  </div>
);

export const TeamsRoles = () => (
  <div>
    <FeatureIcon>
      <EyeOff size={20} />
    </FeatureIcon>
    <h3>Limit data and tool access with predefined roles</h3>
    <p>
      Invite team members to perform key tasks without the ability to change
      your settings or view revenue data.
    </p>
  </div>
);

export const TeamsCustomRoles = () => (
  <div>
    <FeatureIcon>
      <Lock size={20} />
    </FeatureIcon>
    <h3>Create fine-grained custom roles with limited permissions</h3>
    <p>
      Build your own roles comprising dozens of access points including
      individual reports and specific metrics.
    </p>
  </div>
);

export const TeamsExport = () => (
  <div>
    <FeatureIcon>
      <ExternalLink size={20} />
    </FeatureIcon>
    <h3>Export any and all data for outsourcing tasks</h3>
    <p>
      Export your content or link analytics so you can outsource tasks like
      replacing broken affiliate links or refreshing stale content.
    </p>
  </div>
);

export const RevisionsAutomatic = () => (
  <div>
    <FeatureIcon>
      <Settings size={20} />
    </FeatureIcon>
    <h3>Create revisions automatically, without any extra setup</h3>
    <p>
      Affilimate monitors your site for edits and creates new revisions without
      you having to trigger them.
    </p>
  </div>
);

export const RevisionsWorkflow = () => (
  <div>
    <FeatureIcon>
      <Trello size={20} />
    </FeatureIcon>
    <h3>Fits into your existing editorial workflow</h3>
    <p>
      Run experiments on your content as part of your standard workflow, without
      setting up tedious experiments.
    </p>
  </div>
);

export const RevisionsIgnore = () => (
  <div>
    <FeatureIcon>
      <EyeOff size={20} />
    </FeatureIcon>
    <h3>Ignore minor content edits with just two clicks</h3>
    <p>
      Ignore any automatically created revision with two clicks. Restore the
      revision in the future at any time.
    </p>
  </div>
);

export const HeatmapsClicksAndConversions = () => (
  <div>
    <FeatureIcon>
      <DollarSign size={20} />
    </FeatureIcon>
    <h3>Identify which parts of your post drive revenue</h3>
    <p>
      Find the exact strategies that are working on top pages, and scale those
      across your whole site.
    </p>
  </div>
);

export const HeatmapsSpeed = () => (
  <div>
    <FeatureIcon>
      <Zap size={20} />
    </FeatureIcon>
    <h3>Eliminate the speed issues introduced by most heatmap tools</h3>
    <p>
      Affilimate's heatmaps use smarter batching and minimal data collection to
      keep your site fast while capturing heatmaps.
    </p>
  </div>
);

export const HeatmapsCost = () => (
  <div>
    <FeatureIcon>
      <Layers size={20} />
    </FeatureIcon>
    <h3>Build heatmaps for your whole site, not just top pages</h3>
    <p>
      Continuously generate heatmaps for your whole site, without the
      restrictive session limits of most heatmap tools.
    </p>
  </div>
);

export const ContentAnalyticsImpressions = () => (
  <div>
    <FeatureIcon>
      <Eye size={20} />
    </FeatureIcon>
    <h3>Use impression tracking to find hidden opportunities</h3>
    <p>
      Optimize placement of links that are rarely seen but drive clicks and
      conversions. Earn more from your existing content.
    </p>
  </div>
);

export const ContentAnalyticsGrowth = () => (
  <div>
    <FeatureIcon>
      <TrendingUp size={20} />
    </FeatureIcon>
    <h3>Identify trending or seasonal content to optimize now</h3>
    <p>
      Use the Growth report to find pages suddenly growing in clicks or revenue
      so you can optimize them.
    </p>
  </div>
);

export const ContentAnalyticsRevisions = () => (
  <div>
    <FeatureIcon>
      <Rewind size={20} />
    </FeatureIcon>
    <h3>Trace changes in key metrics back to content edits</h3>
    <p>
      Use Affilimate's revision tracking to monitor how content edits impact
      your affiliate links' key metrics like CTR and RPM.
    </p>
  </div>
);

export const ContentAnalyticsDrops = () => (
  <div>
    <FeatureIcon>
      <TrendingDown size={20} />
    </FeatureIcon>
    <h3>Stop revenue drops due to broken links or stock issues</h3>
    <p>
      Identify revenue drops quickly and replace broken links or out of stock
      product recommendations.
    </p>
  </div>
);

export const ContentAnalyticsCompare = () => (
  <div>
    <FeatureIcon>
      <Shuffle size={20} />
    </FeatureIcon>
    <h3>Compare content performance and metrics over time</h3>
    <p>
      All content analytics metrics automatically compare with the previous time
      period, so you can see where growth is coming from.
    </p>
  </div>
);

export const DashboardMetrics = () => (
  <div>
    <FeatureIcon>
      <MousePointer size={20} />
    </FeatureIcon>
    <h3>Track key metrics like clicks and EPC per platform</h3>
    <p>
      Monitor which networks and platforms are receiving the most clicks from
      your website in aggregate or as a timeseries.
    </p>
  </div>
);

export const DashboardReports = () => (
  <div>
    <FeatureIcon>
      <ShoppingCart size={20} />
    </FeatureIcon>
    <h3>Identify your best-performing advertisers and products</h3>
    <p>
      Run advertiser-level reports and see which products are performing best
      for each platform you’re working with.
    </p>
  </div>
);

export const DashboardMultipleConnections = () => (
  <div>
    <FeatureIcon>
      <Minimize2 size={20} />
    </FeatureIcon>
    <h3>Add multiple connections to the same network or platform</h3>
    <p>
      Connect as many accounts as you need to pull affiliate commissions from
      all the websites you operate.
    </p>
  </div>
);

export const DashboardMetadata = () => (
  <div>
    <FeatureIcon>
      <Hash size={20} />
    </FeatureIcon>
    <h3>
      Report on transaction metadata like color, brand, or product category
    </h3>
    <p>
      See detailed transaction metadata, typically hidden by networks, in a
      single format across your commissions.
    </p>
  </div>
);

export const DashboardFilter = () => (
  <div>
    <FeatureIcon>
      <Filter size={20} />
    </FeatureIcon>
    <h3>Run custom reports, filter, group, and sort your commissions</h3>
    <p>
      Run powerful reports using any combination of filters, grouping, and
      sorting or use one of our reporting presets.
    </p>
  </div>
);

export const ContentAnalyticsExport = () => (
  <div>
    <FeatureIcon>
      <ExternalLink size={20} />
    </FeatureIcon>
    <h3>Export and cross-reference your content analytics data</h3>
    <p>
      Export your page-level revenue analytics to cross-reference or combine
      with data from SEO tools or your display ad network.
    </p>
  </div>
);

export const LinkManagementExport = () => (
  <div>
    <FeatureIcon>
      <ExternalLink size={20} />
    </FeatureIcon>
    <h3>Export your affiliate links alongside all metrics</h3>
    <p>
      Export your affiliate links as CSV or Excel to outsource common tasks like
      replacing broken affiliate links.
    </p>
  </div>
);

export const LinkGeneratorSpeed = () => (
  <div>
    <FeatureIcon>
      <Zap size={20} />
    </FeatureIcon>
    <h3>Create affiliate links faster than ever before</h3>
    <p>
      Stop trying to remember which platform hosts a specific merchant, and just
      paste the product link instead.
    </p>
  </div>
);

export const LinkGeneratorDelegation = () => (
  <div>
    <FeatureIcon>
      <EyeOff size={20} />
    </FeatureIcon>
    <h3>Delegate link creation without sharing revenue data</h3>
    <p>
      Invite users to your account with Tools Only permissions, so they can
      create affiliate links without seeing financial data.
    </p>
  </div>
);

export const LinkGeneratorCustomize = () => (
  <div>
    <FeatureIcon>
      <Settings size={20} />
    </FeatureIcon>
    <h3>Customize your links SubIDs and channel attributes</h3>
    <p>
      Create the optimal link for tracking by customizing your links based on
      which channel or website they'll be shared on.
    </p>
  </div>
);

export const DashboardExport = () => (
  <div>
    <FeatureIcon>
      <ExternalLink size={20} />
    </FeatureIcon>
    <h3>Export your commissions in a unified format to CSV or XLSX</h3>
    <p>
      Get a single, unified data model for all your affiliate commissions and
      export them for Google Sheets or Excel.
    </p>
  </div>
);

export const DashboardTimeline = () => (
  <div>
    <FeatureIcon>
      <GitMerge size={20} />
    </FeatureIcon>
    <h3>Monitor your commission's lifecycle from click to payout</h3>
    <p>
      Watch key events in your commission's lifecycle from the origin of the
      click to purchase, locking date, and payment.
    </p>
  </div>
);

export const DashboardAttribution = () => (
  <div>
    <FeatureIcon>
      <Shuffle size={20} />
    </FeatureIcon>
    <h3>Attribute commissions through SubIDs or rules</h3>
    <p>
      Attribute commissions to your content through dynamically generated SubIDs
      or use our rules-based system.
    </p>
  </div>
);

export const LinkManagementTracking = () => (
  <div>
    <FeatureIcon>
      <MousePointer size={20} />
    </FeatureIcon>
    <h3>Track affiliate link clicks across your whole website automatically</h3>
    <p>
      Start tracking all the existing affiliate links across your website,
      without having cloak or shorten them first.
    </p>
  </div>
);

export const LinkManagementSingleLibrary = () => (
  <div>
    <FeatureIcon>
      <BookOpen size={20} />
    </FeatureIcon>
    <h3>Create a single library of affiliate links from multiple websites</h3>
    <p>
      Create a single library of affiliate links you can search, sort, filter,
      export, copy and paste across pages and websites.
    </p>
  </div>
);

export const LinkManagementRevenue = () => (
  <div>
    <FeatureIcon>
      <DollarSign size={20} />
    </FeatureIcon>
    <h3>See clicks, revenue, and EPC for each affiliate link</h3>
    <p>
      Discover which affiliate links on your website are responsible for driving
      the most revenue and which are underperforming.
    </p>
  </div>
);

export const LinkManagementPerPage = () => (
  <div>
    <FeatureIcon>
      <File size={20} />
    </FeatureIcon>
    <h3>See link performance per page for key metrics</h3>
    <p>
      Drill down to see how your affiliate links perform on individual pages and
      posts in terms of revenue, EPC, CTR, and clicks.
    </p>
  </div>
);

export const LinkManagementAmazon = () => (
  <div>
    <FeatureIcon>
      <Box size={20} />
    </FeatureIcon>
    <h3>Fix broken links with the Amazon affiliate link checker</h3>
    <p>
      Identify the Amazon links on your website which take visitors to
      out-of-stock products, and fix the ones being clicked most often.
    </p>
  </div>
);

export const LinkManagementHeatmaps = () => (
  <div>
    <FeatureIcon>
      <Target size={20} />
    </FeatureIcon>
    <h3>Visualize your affiliate links clicks on heatmaps</h3>
    <p>
      Take heatmaps of your entire website to visualize which links are getting
      clicks and driving conversions.
    </p>
  </div>
);

export const DashboardTeams = () => (
  <div>
    <FeatureIcon>
      <Users size={20} />
    </FeatureIcon>
    <h3>Delegate key tasks to your team members</h3>
    <p>
      Add team members to your account and delegate the creation of common
      reports or data import and export.
    </p>
  </div>
);

export const FeatureAffiliateDashboard = () => (
  <FeatureCard>
    <FeatureIcon>
      <Layout size={24} />
    </FeatureIcon>
    <h3>
      <FeatureLink>Unified Dashboard</FeatureLink>
    </h3>
    <p>Aggregate all your affiliate commissions in one, unified dashboard.</p>
  </FeatureCard>
);

export const FeatureAffiliateApis = () => (
  <FeatureCard>
    <FeatureIcon>
      <Terminal size={24} />
    </FeatureIcon>
    <h3>
      <FeatureLink>Advanced APIs</FeatureLink>
    </h3>
    <p>
      Pull your data into a custom warehouse or use it to promote trending
      content.
    </p>
  </FeatureCard>
);

export const FeatureExports = () => (
  <FeatureCard>
    <FeatureIcon>
      <Download size={24} />
    </FeatureIcon>
    <h3>
      <FeatureLink>Comprehensive Export</FeatureLink>
    </h3>
    <p>Export any report or dataset to CSV or Excel for further analysis.</p>
  </FeatureCard>
);

export const FeatureContentAnalytics = () => (
  <FeatureCard>
    <FeatureIcon>
      <File />
    </FeatureIcon>
    <h3>
      <FeatureLink>In-Article Analytics</FeatureLink>
    </h3>
    <p>
      Detailed, link-level in-article analytics presented in a user-friendly
      way.
    </p>
  </FeatureCard>
);

export const FeatureRevenueAttribution = () => (
  <FeatureCard>
    <FeatureIcon>
      <DollarSign />
    </FeatureIcon>
    <h3>
      <FeatureLink>Granular Attribution</FeatureLink>
    </h3>
    <p>
      See affiliate performance by site, page, link, element, merchant and more.
    </p>
  </FeatureCard>
);

export const FeatureConversionHeatmaps = () => (
  <FeatureCard>
    <FeatureIcon>
      <Target />
    </FeatureIcon>
    <h3>
      <FeatureLink>Conversion-aware Heatmaps</FeatureLink>
    </h3>
    <p>
      Heatmaps of affiliate conversions and engagement within your articles.
    </p>
  </FeatureCard>
);

export const FeatureAuthors = () => (
  <FeatureCard>
    <FeatureIcon>
      <Edit />
    </FeatureIcon>
    <h3>
      <FeatureLink>Author Insights</FeatureLink>
    </h3>
    <p>Benchmark author performance for key affiliate metrics automatically.</p>
  </FeatureCard>
);

export const FeatureRates = () => (
  <FeatureCard>
    <FeatureIcon>
      <Percent />
    </FeatureIcon>
    <h3>
      <FeatureLink>Rate Tracking</FeatureLink>
    </h3>
    <p>Monitor your rates cross-network with daily updates.</p>
  </FeatureCard>
);

export const FeaturePayouts = () => (
  <FeatureCard>
    <FeatureIcon>
      <Shuffle />
    </FeatureIcon>
    <h3>
      <FeatureLink>Payouts &amp; Reconciliation</FeatureLink>
    </h3>
    <p>
      Streamline finance processes with payout reporting and reconciliation.
    </p>
  </FeatureCard>
);

export const FeatureRevisionTracking = () => (
  <FeatureCard>
    <FeatureIcon>
      <Rewind />
    </FeatureIcon>
    <h3>
      <FeatureLink>Revisions</FeatureLink>
    </h3>
    <p>Run experiments and monitor key metrics for each content edit.</p>
  </FeatureCard>
);

export const FeatureTrafficSources = () => (
  <FeatureCard>
    <FeatureIcon>
      <GitMerge />
    </FeatureIcon>
    <h3>
      <FeatureLink>Traffic Sources &amp; UTMs</FeatureLink>
    </h3>
    <p>
      Track affiliate earnings back to traffic source, UTM, and paid campaigns.
    </p>
  </FeatureCard>
);

export const FeatureTeams = () => (
  <FeatureCard>
    <FeatureIcon>
      <Users />
    </FeatureIcon>
    <h3>
      <FeatureLink>Teams &amp; Workspaces</FeatureLink>
    </h3>
    <p>Invite team members to your workspace and manage multiple workspaces.</p>
  </FeatureCard>
);

export const FeatureRoles = () => (
  <FeatureCard>
    <FeatureIcon>
      <EyeOff />
    </FeatureIcon>
    <h3>
      <FeatureLink>Custom Roles &amp; Permissions</FeatureLink>
    </h3>
    <p>Design custom user roles giving just the level of visibility needed.</p>
  </FeatureCard>
);

export const FeatureIntegrations = () => (
  <FeatureCard>
    <FeatureIcon>
      <Minimize2 />
    </FeatureIcon>
    <h3>
      <FeatureLink>100+ Integrations</FeatureLink>
    </h3>
    <p>
      Consolidate earnings from 100+ affiliate networks, platforms, and data
      sources.
    </p>
  </FeatureCard>
);

export const FeatureCustomIntegrations = () => (
  <FeatureCard>
    <FeatureIcon>
      <Box />
    </FeatureIcon>
    <h3>
      <FeatureLink>Custom Integrations</FeatureLink>
    </h3>
    <p>Expertly maintained custom integrations for the full revenue picture.</p>
  </FeatureCard>
);

export const FeatureLinkManagement = () => (
  <FeatureCard>
    <FeatureIcon>
      <LinkIcon />
    </FeatureIcon>
    <h3>
      <FeatureLink>Affiliate Link Management</FeatureLink>
    </h3>
    <p>
      Automate your affiliate link management, including with custom redirects.
    </p>
  </FeatureCard>
);

export const FeatureLinkGenerator = () => (
  <FeatureCard>
    <FeatureIcon>
      <Zap />
    </FeatureIcon>
    <h3>
      <FeatureLink>Affiliate Link Generator</FeatureLink>
    </h3>
    <p>
      Scale your link creation with the universal affiliate link generator tool
      or API.
    </p>
  </FeatureCard>
);

export const FeatureSegmentation = () => (
  <FeatureCard>
    <FeatureIcon>
      <PieChart />
    </FeatureIcon>
    <h3>
      <FeatureLink>Scalable Segmentation</FeatureLink>
    </h3>
    <p>
      Segment your affiliate content by content format, product category, and
      more.
    </p>
  </FeatureCard>
);

export const AuthorAutomation = () => (
  <div>
    <FeatureIcon>
      <Zap size={20} />
    </FeatureIcon>
    <h3>Assign authors to your content automatically</h3>
    <p>
      Sync your authors to their content without any manual setup, based on your
      existing markup.
    </p>
  </div>
);

export const AuthorGroups = () => (
  <div>
    <FeatureIcon>
      <Users size={20} />
    </FeatureIcon>
    <h3>Filter and compare custom author groups</h3>
    <p>
      Filter your author reports to compare performance between just a subset of
      your authors.
    </p>
  </div>
);

export const AuthorSync = () => (
  <div>
    <FeatureIcon>
      <RefreshCw size={20} />
    </FeatureIcon>
    <h3>Sync added and removed authors post-publish</h3>
    <p>
      Keep your author reports totally in sync with your CMS, regardless of
      whether authors are added or removed after publishing.
    </p>
  </div>
);

export const TrafficSources = () => (
  <div>
    <FeatureIcon>
      <GitMerge size={20} />
    </FeatureIcon>
    <h3>Discover new, high-performing traffic sources</h3>
    <p>
      Break down your affiliate revenue by referrer groups like Search and
      Social. Then breakdown performance by domain to discover new, effective
      traffic sources.
    </p>
  </div>
);

export const TrafficSourcesByArticle = () => (
  <div>
    <FeatureIcon>
      <Code size={20} />
    </FeatureIcon>
    <h3>View top articles by traffic source and UTM parameter</h3>
    <p>
      See which articles perform best from different traffic sources,
      considering both engagement and revenue. Then, drill down to see which
      UTMs are the driving force.
    </p>
  </div>
);

export const TrafficSourcesAudienceDevelopment = () => (
  <div>
    <FeatureIcon>
      <Users size={20} />
    </FeatureIcon>
    <h3>Partner with Audience Development, Social, or Sales teams</h3>
    <p>
      Collaborate with colleagues in aud dev, social, or sales teams to optimize
      paid search or paid social campaigns on the fly. Factor in true
      performance, not just clicks or CTR.
    </p>
  </div>
);

export const TrafficSourcesPaidCampaigns = () => (
  <div>
    <FeatureIcon>
      <DollarSign size={20} />
    </FeatureIcon>
    <h3>Feed affiliate conversion data back to paid search tools</h3>
    <p>
      Pull your conversion data into platforms like Google Ads via API, so you
      can optimize performance of your paid search or social campaigns.
    </p>
  </div>
);

export const TrafficSourcesOrganicIsolation = () => (
  <div>
    <FeatureIcon>
      <Search size={20} />
    </FeatureIcon>
    <h3>Filter out lower-intent traffic and performance</h3>
    <p>
      Isolate your affiliate performance to organic search. Filter out
      lower-intent traffic like social, paid, or Google Discover from your EPC
      and RPM calculations.
    </p>
  </div>
);

export const TrafficSourcesNewsletter = () => (
  <div>
    <FeatureIcon>
      <Mail size={20} />
    </FeatureIcon>
    <h3>Measure performance of your newsletter traffic</h3>
    <p>
      Trace performance of your newsletter traffic, back to the email campaign
      through UTM parameters. See which articles drove the most engagement and
      earnings via email.
    </p>
  </div>
);

export const FeatureGrid: React.FC = ({ children }) => {
  return (
    <GridWrapper>
      <Grid>{children}</Grid>
    </GridWrapper>
  );
};
